// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import {
  AttendanceVerificationMethod,
  type AttendanceVerificationMethodConfig,
  ExternalIntegrationTypes,
  NfcTagRequestStatus,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import { useGetWorkplaceExternalIntegrations } from "@src/appV2/ExternalIntegrations/api/useGetWorkplaceExternalIntegrations";
import {
  getWorkplaceQueryKey,
  useGetWorkplace,
} from "@src/appV2/FacilityProfile/api/useGetWorkplace";
import { useUpdateWorkerCredentialConfiguration } from "@src/appV2/FacilityProfile/api/useUpdateWorkerCredentialConfiguration";
import { useGetFacilityUserDetails } from "@src/appV2/FacilityUser/api";
import { useToast } from "@src/appV2/lib";
import { Text } from "@src/lib/ui-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetAttendanceVerification } from "./api/useGetAttendanceVerification";
import { useUpdateAttendanceVerification } from "./api/useUpdateAttendanceVerification";
import { AttendanceVerificationMethodItem } from "./components/AttendanceVerificationMethodItem";
import { SortableItem } from "./components/SortableItem";
import {
  createDefaultMethodConfig,
  hasTutorialConfig,
  hasValidationModels,
  type NfcTag,
  type SaveNfcTagLocationParams,
  TIMEKEEPING_METHODS_REQUIRING_CREDENTIALS,
} from "./types";

interface AttendanceVerificationMethodsProps {
  workplaceId: string;
  isCbhAdmin: boolean;
  nfcRequestList?: NfcTag[];
  onSaveNfcTagLocation?: (params: SaveNfcTagLocationParams) => Promise<void>;
  onDeleteNfcTag?: (tagId: string) => Promise<void>;
  onRequestNfcTags?: (numberOfTags: number) => Promise<void>;
}

const EXTERNAL_INTEGRATION_METHODS = [
  AttendanceVerificationMethod.UKG_INTEGRATION,
  AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
  AttendanceVerificationMethod.MESH_INTEGRATION,
] as const;

// For now, only UKG has external integration config to display.
const ATTENDANCE_METHOD_TO_EXTERNAL_INTEGRATION_TYPE: Partial<
  Record<AttendanceVerificationMethod, ExternalIntegrationTypes>
> = {
  [AttendanceVerificationMethod.UKG_INTEGRATION]: ExternalIntegrationTypes.UKG,
};

/**
 * All available method types that should always be shown.
 */
const NON_EXTERNAL_INTEGRATION_METHODS = [
  AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
  AttendanceVerificationMethod.TAKE_A_VIDEO,
  AttendanceVerificationMethod.NFC_TAG,
  AttendanceVerificationMethod.GEOFENCE,
] as const;

const VERIFICATION_METHODS_REQUIRING_TUTORIAL: AttendanceVerificationMethod[] = [
  AttendanceVerificationMethod.UKG_INTEGRATION,
  AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
];

/**
 * Normalize a method config by merging with defaults to ensure all required fields exist.
 */
function normalizeMethodConfig(
  method: AttendanceVerificationMethodConfig
): AttendanceVerificationMethodConfig {
  const defaultConfig = createDefaultMethodConfig(method.type);
  return { ...defaultConfig, ...method };
}

/**
 * Get all methods, merging API data with defaults for missing methods.
 * This ensures all method types are always displayed.
 */
function getAllMethods(
  apiMethods: AttendanceVerificationMethodConfig[] | undefined
): AttendanceVerificationMethodConfig[] {
  const defaultMethods = NON_EXTERNAL_INTEGRATION_METHODS.map((type) =>
    createDefaultMethodConfig(type)
  );

  if (!apiMethods || apiMethods.length === 0) {
    return defaultMethods;
  }

  // Create a map of API methods by type for quick lookup
  const apiMethodsByType = new Map(apiMethods.map((m) => [m.type, m]));

  // Start with normalized API methods in their order
  const result: AttendanceVerificationMethodConfig[] = apiMethods.map(normalizeMethodConfig);

  const apiHasExternalIntegrationMethod = EXTERNAL_INTEGRATION_METHODS.some(
    (externalIntegrationMethod) => apiMethodsByType.has(externalIntegrationMethod)
  );
  if (!apiHasExternalIntegrationMethod) {
    result.push(createDefaultMethodConfig(AttendanceVerificationMethod.UKG_INTEGRATION));
  }

  // Add any missing method types (disabled by default)
  for (const defaultMethod of defaultMethods) {
    if (!apiMethodsByType.has(defaultMethod.type)) {
      result.push(defaultMethod);
    }
  }

  return result;
}

function getMethodId(method: AttendanceVerificationMethodConfig, index: number): string {
  return `${method.type}-${index}`;
}

export function AttendanceVerificationMethods(props: AttendanceVerificationMethodsProps) {
  const {
    workplaceId,
    nfcRequestList,
    isCbhAdmin,
    onSaveNfcTagLocation,
    onDeleteNfcTag,
    onRequestNfcTags,
  } = props;

  const { showErrorToast, showSuccessToast } = useToast();

  const {
    data: attendanceVerification,
    isLoading,
    isError,
    error,
  } = useGetAttendanceVerification(workplaceId);

  const { data: workplaceUserDetails } = useGetFacilityUserDetails(
    isCbhAdmin ? workplaceId : undefined
  );
  const parentWorkplaceId = workplaceUserDetails?.facility?.parentFacilityId;

  const { data: externalIntegrations } = useGetWorkplaceExternalIntegrations({
    workplaceId,
    enabled: isCbhAdmin,
  });

  const { data: parentExternalIntegrations } = useGetWorkplaceExternalIntegrations({
    workplaceId: parentWorkplaceId ?? "",
    enabled: isCbhAdmin && isDefined(parentWorkplaceId),
    filterEnabled: true,
  });

  const { mutateAsync: updateAttendanceVerification, isLoading: isUpdating } =
    useUpdateAttendanceVerification();

  const queryClient = useQueryClient();
  const { data: workplaceResponse } = useGetWorkplace({ workplaceId });
  const { mutateAsync: updateWorkerCredentialConfiguration } =
    useUpdateWorkerCredentialConfiguration();

  const externalIntegrationsByType = useMemo(() => {
    return new Map(
      (externalIntegrations ?? []).map((integration) => [integration.type, integration])
    );
  }, [externalIntegrations]);

  const parentExternalIntegrationsByType = useMemo(() => {
    return new Map(
      (parentExternalIntegrations ?? []).map((integration) => [integration.type, integration])
    );
  }, [parentExternalIntegrations]);

  const [methods, setMethods] = useState<AttendanceVerificationMethodConfig[]>([]);

  useEffect(() => {
    if (!isLoading) {
      const apiMethods = attendanceVerification?.data?.attributes?.methods;
      const allMethods = getAllMethods(apiMethods);
      setMethods(allMethods);
    }
  }, [attendanceVerification, isLoading]);

  const sensors = useSensors(useSensor(MouseSensor));

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!isDefined(over) || active.id === over.id) {
        return;
      }

      const oldIndex = methods.findIndex(
        (_, index) => getMethodId(methods[index], index) === active.id
      );
      const newIndex = methods.findIndex(
        (_, index) => getMethodId(methods[index], index) === over.id
      );

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const newMethods = [...methods];
      const [removed] = newMethods.splice(oldIndex, 1);
      newMethods.splice(newIndex, 0, removed);

      const toSaveMethods = newMethods.filter((method) => {
        const hasMethodConfiguration = attendanceVerification?.data?.attributes?.methods?.some(
          (currentMethod) => currentMethod.type === method.type
        );
        return method.isEnabled || hasMethodConfiguration;
      });

      setMethods(newMethods);
      await updateAttendanceVerification({
        workplaceId,
        methods: toSaveMethods,
      });
    },
    [methods, workplaceId, updateAttendanceVerification, attendanceVerification]
  );

  /**
   * Returns true if timekeeping was already enabled or was successfully enabled.
   * Returns false if we couldn't enable it (workplace data not loaded yet).
   */
  const ensureTimekeepingEnabled = useCallback(async (): Promise<boolean> => {
    if (!isDefined(workplaceResponse)) {
      showErrorToast("Please wait for workplace data to load and try again");
      return false;
    }

    const config = workplaceResponse.data.attributes.workerCredentialConfiguration;
    if (config?.timekeeping.enabled && config?.onboardingView.enabled) {
      return true;
    }

    await updateWorkerCredentialConfiguration({
      workplaceId,
      workerCredentialConfiguration: {
        charting: { enabled: config?.charting.enabled ?? false },
        timekeeping: { enabled: true },
        onboardingView: {
          enabled: true,
          shiftStartingSoonThreshold: {
            value: config?.onboardingView.shiftStartingSoonThreshold.value ?? 24,
            unit: "hours",
          },
        },
      },
    });
    await queryClient.invalidateQueries([getWorkplaceQueryKey(workplaceId)]);
    showSuccessToast(
      "Worker credential configuration for timekeeping and onboarding view has been automatically enabled"
    );
    return true;
  }, [
    workplaceResponse,
    workplaceId,
    updateWorkerCredentialConfiguration,
    queryClient,
    showSuccessToast,
    showErrorToast,
  ]);

  const onSaveMethod = useCallback(
    async (index: number, updatedConfig: AttendanceVerificationMethodConfig) => {
      const newMethods = methods.map((method, methodIndex) =>
        methodIndex === index ? updatedConfig : method
      );

      const enabledCount = newMethods.filter((m) => m.isEnabled).length;
      if (enabledCount === 0) {
        showErrorToast("At least one attendance verification method must be active");
        return;
      }

      const isEnablingTimekeepingMethod =
        updatedConfig.isEnabled &&
        !methods[index].isEnabled &&
        TIMEKEEPING_METHODS_REQUIRING_CREDENTIALS.includes(updatedConfig.type);

      if (isEnablingTimekeepingMethod) {
        const success = await ensureTimekeepingEnabled();
        if (!success) {
          return;
        }
      }

      const savedMethodType = updatedConfig.type;
      const toSaveMethods = newMethods.filter((method) => {
        if (method.type === savedMethodType) {
          return true;
        }
        const hasMethodConfiguration = attendanceVerification?.data?.attributes?.methods?.some(
          (currentMethod) => currentMethod.type === method.type
        );
        return method.isEnabled || hasMethodConfiguration;
      });

      await updateAttendanceVerification({
        workplaceId,
        methods: toSaveMethods,
      });
      setMethods(newMethods);
    },
    [
      methods,
      workplaceId,
      updateAttendanceVerification,
      attendanceVerification,
      showErrorToast,
      ensureTimekeepingEnabled,
    ]
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ padding: 4 }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  if (isError) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    return (
      <Alert severity="error" sx={{ marginTop: 2 }}>
        <Text>Failed to load timekeeping methods configuration.</Text>
        {errorMessage && <Text>{errorMessage}</Text>}
      </Alert>
    );
  }

  interface MethodToggleResult {
    allowed: boolean;
    reason?: string;
  }

  function getMissingRequiredFields(
    method: AttendanceVerificationMethodConfig
  ): string[] | undefined {
    if (!hasTutorialConfig(method)) {
      return undefined;
    }

    const missingFields: string[] = [];

    if (!method.timeclockLocationString?.trim()) {
      missingFields.push("Time clock location");
    }

    const hasTutorialSteps = VERIFICATION_METHODS_REQUIRING_TUTORIAL.includes(method.type);

    if (hasTutorialSteps) {
      if (method.tutorial.stepsClockIn.length === 0) {
        missingFields.push("Steps for clocking in");
      }
      if (method.tutorial.stepsClockOut.length === 0) {
        missingFields.push("Steps for clocking out");
      }
    }

    if (method.type === AttendanceVerificationMethod.AI_TAKE_A_VIDEO) {
      if (!hasValidationModels(method)) {
        missingFields.push("AI validation model");
      }
    }

    return missingFields.length > 0 ? missingFields : undefined;
  }

  function isMethodToggleAllowed(method: AttendanceVerificationMethodConfig): MethodToggleResult {
    if (method.type === AttendanceVerificationMethod.GEOFENCE) {
      return {
        allowed: false,
        reason: "Geofence is always checked on timekeeping actions.",
      };
    }

    if (!method.isEnabled) {
      const reasons: string[] = [];

      if (method.type === AttendanceVerificationMethod.UKG_INTEGRATION) {
        const ukgIntegration = externalIntegrationsByType.get(ExternalIntegrationTypes.UKG);
        if (
          !isDefined(ukgIntegration) ||
          !ukgIntegration.isEnabled ||
          !isDefined(ukgIntegration.ukg) ||
          !("ukgBusinessUnitName" in ukgIntegration.ukg)
        ) {
          reasons.push(
            "UKG business unit name must be provided and the external integration configuration must be enabled"
          );
        }
      }

      const missingFields = getMissingRequiredFields(method);
      if (missingFields) {
        reasons.push(`${missingFields.join(", ")} must be provided`);
      }

      if (reasons.length > 0) {
        return {
          allowed: false,
          reason: `${reasons.join(". ")}.`,
        };
      }
    }

    if (method.type === AttendanceVerificationMethod.NFC_TAG) {
      if (!method.isEnabled) {
        const nfcTagsInSetUp = (nfcRequestList ?? []).filter(
          (tag) => tag.tagStatus === NfcTagRequestStatus.SET_UP
        );

        if (nfcTagsInSetUp.length === 0) {
          return {
            allowed: false,
            reason:
              "At least one NFC tag must be in Set up status before enabling this method. Set up a tag location first.",
          };
        }
      }

      return { allowed: true };
    }

    if (isCbhAdmin) {
      return { allowed: true };
    }

    return {
      allowed: false,
      reason: "Please contact support to change this method for your workplace",
    };
  }

  function isMethodReorderAllowed(): boolean {
    return isCbhAdmin;
  }

  const methodIds = methods.map((method, index) => getMethodId(method, index));
  const firstEnabledIndex = methods.findIndex((method) => method.isEnabled);

  return (
    <Stack spacing={2} sx={{ position: "relative", opacity: isUpdating ? 0.7 : 1 }}>
      <Box>
        <Text variant="body2" sx={{ marginBottom: 1 }}>
          Timekeeping Methods
        </Text>
        <Text variant="body2" color="text.secondary">
          Choose the active timekeeping methods for this workplace and order them by priority. There
          must always be at least one method active, and the default active one is Geofence.
        </Text>
      </Box>

      {isUpdating && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={methodIds} strategy={verticalListSortingStrategy}>
          {methods.map((method, index) => {
            const id = getMethodId(method, index);
            const toggleResult = isMethodToggleAllowed(method);
            const canReorder = isMethodReorderAllowed();
            const externalIntegrationType =
              ATTENDANCE_METHOD_TO_EXTERNAL_INTEGRATION_TYPE[method.type];
            const externalIntegration = isDefined(externalIntegrationType)
              ? externalIntegrationsByType.get(externalIntegrationType)
              : undefined;
            const parentExternalIntegration = isDefined(externalIntegrationType)
              ? parentExternalIntegrationsByType.get(externalIntegrationType)
              : undefined;

            return (
              <SortableItem key={id} id={id} disabled={!canReorder}>
                <AttendanceVerificationMethodItem
                  id={id}
                  method={method}
                  isFirstEnabledMethod={index === firstEnabledIndex}
                  totalMethods={methods.length}
                  canToggle={toggleResult.allowed}
                  canExpand={
                    isCbhAdmin ||
                    toggleResult.allowed ||
                    method.type === AttendanceVerificationMethod.NFC_TAG
                  }
                  disabledReason={toggleResult.reason}
                  canReorder={canReorder}
                  workplaceId={workplaceId}
                  isCbhAdmin={isCbhAdmin}
                  externalIntegration={externalIntegration}
                  parentExternalIntegration={parentExternalIntegration}
                  nfcRequestList={
                    method.type === AttendanceVerificationMethod.NFC_TAG
                      ? nfcRequestList
                      : undefined
                  }
                  onSave={async (config) => {
                    await onSaveMethod(index, config);
                  }}
                  onSaveNfcTagLocation={
                    method.type === AttendanceVerificationMethod.NFC_TAG
                      ? onSaveNfcTagLocation
                      : undefined
                  }
                  onDeleteNfcTag={
                    method.type === AttendanceVerificationMethod.NFC_TAG
                      ? onDeleteNfcTag
                      : undefined
                  }
                  onRequestNfcTags={
                    method.type === AttendanceVerificationMethod.NFC_TAG
                      ? onRequestNfcTags
                      : undefined
                  }
                />
              </SortableItem>
            );
          })}
        </SortableContext>
      </DndContext>
    </Stack>
  );
}
